import { fmt } from '@nyce/admin-kit';
import { Kpi } from '../components/common';
import type { UsagePeriodName, UsageTotals } from './api/usage';
import './buddy.css';

// nyce.buddy statistics building blocks (the Buddy dashboard and the Usage page), reviewed from buddy3's admin
// `components/stats` (KpiGrid, Delta, LimitBar, format).

export const money = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(2)}`);

/** Ratio 0..1 → `42 %` (one decimal below 10 %). */
export const pct = (v: number | null | undefined) =>
  v == null || !Number.isFinite(v)
    ? '—'
    : `${v * 100 < 10 && v > 0 ? (v * 100).toFixed(1) : Math.round(v * 100)} %`;

export const duration = (ms: number | null | undefined) => {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${String(Math.round((ms % 60_000) / 1000)).padStart(2, '0')} s`;
};

export const compact = (v: number | null | undefined) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)} B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)} M`;
  if (a >= 1e4) return `${Math.round(v / 1000)} k`;
  return fmt(v);
};

/** Share of the real tokens spent by agents (monitors, scheduled cards), 0..1. */
export const agentShare = (t: Pick<UsageTotals, 'tokens' | 'agentTokens'>) =>
  t.agentTokens == null ? null : t.tokens > 0 ? t.agentTokens / t.tokens : 0;

export const PERIODS: { value: UsagePeriodName; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'month', label: 'This month' },
  { value: 'prevMonth', label: 'Last month' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

export const periodLabel = (period: UsagePeriodName, from?: string, to?: string) =>
  period === 'custom' && from && to
    ? `${from} – ${to}`
    : (PERIODS.find((p) => p.value === period)?.label ?? period);

export const HELP = {
  realTokens:
    'Tokens the model processed in this period, as billed by the provider: prompt, cached and output.',
  nyceTokens:
    'Real tokens weighted by the model’s token factor (cheaper models and cached tokens count for less). This is what the monthly limits count.',
  cost: 'Estimated provider cost in USD from buddy3’s model pricing table.',
  questions: 'Questions asked by people and agents. One question can take several model requests.',
  requests: 'Model calls: every turn of the agent loop, routing and summary refreshes included.',
  activeUsers:
    'People with at least one question in the period; agents (the pseudo user "monitor") excluded.',
  cacheHitRate: 'Share of the prompt tokens served from the model’s context cache.',
  responseTime: 'Question to final answer. p50 is the typical turn, p95 the slow tail.',
  feedback: 'Thumbs up and down left on answers in this period.',
  failedQueries: 'Literal queries the workservers rejected in this period.',
  errorRate: 'Share of chat, replay and MCP requests that ended in a server error (HTTP 5xx).',
} as const;

/** Change versus the previous period; `good` = the desirable direction ('neutral' colours nothing). */
export function Delta({
  current,
  previous,
  good = 'neutral',
  label,
}: {
  current: number | null | undefined;
  previous: number | null | undefined;
  good?: 'up' | 'down' | 'neutral';
  label?: string;
}) {
  if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous))
    return null;
  if (previous === 0 && current === 0) return <span className="bd-delta">±0 %</span>;
  const change = previous === 0 ? null : (current - previous) / Math.abs(previous);
  const dir = current > previous ? 'up' : current < previous ? 'down' : 'flat';
  const tone = good === 'neutral' || dir === 'flat' ? '' : dir === good ? ' bd-delta-good' : ' bd-delta-bad';
  const text =
    change == null
      ? 'new'
      : `${change > 0 ? '+' : ''}${Math.abs(change) >= 0.1 ? Math.round(change * 100) : (change * 100).toFixed(1)} %`;
  return (
    <span
      className={`bd-delta${tone}`}
      data-delta={dir}
      title={`${label ? `${label}: ` : ''}previous period ${previous.toLocaleString()}`}
      aria-label={`${label ? `${label} ` : ''}${dir === 'flat' ? 'unchanged' : `${dir} ${text}`} versus the previous period`}
    >
      <span aria-hidden="true">{dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→'}</span> {text}
    </span>
  );
}

/** Month-to-date NYCE tokens against a limit, with the projected month end as a marker. */
export function LimitMeter({
  used,
  limit,
  unlimited,
  projected,
}: {
  used: number | null | undefined;
  limit: number | null | undefined;
  unlimited?: boolean;
  projected?: number | null;
}) {
  if (unlimited || !limit) return <span className="nyk-ui-hint">{unlimited ? 'unlimited' : 'no limit'}</span>;
  const p = Math.round(((used ?? 0) / limit) * 100);
  const proj = projected != null ? Math.round((projected / limit) * 100) : null;
  const tone = p >= 100 ? ' oph-meter-red' : (proj ?? p) >= 100 || p >= 80 ? ' oph-meter-amber' : '';
  return (
    <span
      className="oph-meter bd-limit"
      title={`${fmt(used ?? 0)} of ${fmt(limit)} NYCE tokens this month${proj != null ? `, projected ${proj} %` : ''}`}
    >
      <span className="oph-meter-text">
        {p} %{proj != null && proj !== p && <span className="nyk-ui-hint"> → {proj} %</span>}
      </span>
      <span className="oph-meter-track" aria-hidden="true">
        <span className={`oph-meter-fill${tone}`} style={{ width: `${Math.min(100, p)}%` }} />
        {proj != null && proj > p && (
          <span className="bd-limit-projected" style={{ left: `${Math.min(100, proj)}%` }} />
        )}
      </span>
    </span>
  );
}

/** The KPI row of the Usage page, its drill-downs and the Buddy dashboard; tiles without data are left out. */
export function KpiGrid({
  totals: t,
  compare,
  core,
}: {
  totals: UsageTotals;
  compare?: boolean;
  core?: boolean;
}) {
  const p = compare ? t.previous : undefined;
  const d = (
    cur: number | null | undefined,
    prev: number | null | undefined,
    good: 'up' | 'down' | 'neutral',
    label: string
  ) => (p ? <Delta current={cur} previous={prev} good={good} label={label} /> : undefined);
  const share = agentShare(t);
  const humans = t.humanUsers ?? t.activeUsers;
  const fb = t.feedback;
  return (
    <div className="nyk-kpis" data-testid="buddy-kpis">
      <Kpi
        label="Questions"
        value={fmt(t.questions)}
        help={HELP.questions}
        delta={d(t.questions, p?.questions, 'up', 'Questions')}
        sub={t.conversations != null ? `${fmt(t.conversations)} conversations` : undefined}
      />
      <Kpi
        label="Active users"
        value={fmt(humans)}
        help={HELP.activeUsers}
        delta={d(humans, p?.humanUsers ?? p?.activeUsers, 'up', 'Active users')}
      />
      <Kpi
        label="NYCE tokens"
        value={compact(t.weightedTokens)}
        help={HELP.nyceTokens}
        delta={d(t.weightedTokens, p?.weightedTokens, 'neutral', 'NYCE tokens')}
        sub="counted against limits"
      />
      <Kpi
        label="Cost"
        value={money(t.cost)}
        help={HELP.cost}
        delta={d(t.cost, p?.cost, 'neutral', 'Cost')}
        sub={t.agentCost != null ? `${money(t.agentCost)} by agents` : undefined}
      />
      {!core && (
        <>
          <Kpi
            label="Real tokens"
            value={compact(t.tokens)}
            help={HELP.realTokens}
            delta={d(t.tokens, p?.tokens, 'neutral', 'Real tokens')}
            sub={share != null ? `${pct(share)} by agents` : undefined}
          />
          <Kpi
            label="Requests"
            value={fmt(t.requests)}
            help={HELP.requests}
            delta={d(t.requests, p?.requests, 'neutral', 'Requests')}
            sub="model calls"
          />
          {t.cacheHitRate !== undefined && (
            <Kpi
              label="Cache hit rate"
              value={pct(t.cacheHitRate)}
              help={HELP.cacheHitRate}
              delta={d(t.cacheHitRate, p?.cacheHitRate, 'up', 'Cache hit rate')}
            />
          )}
          {(t.p50DurationMs !== undefined || t.p95DurationMs !== undefined) && (
            <Kpi
              label="Response time"
              value={
                <>
                  {duration(t.p50DurationMs)}
                  <span className="nyk-ui-hint"> / {duration(t.p95DurationMs)}</span>
                </>
              }
              help={HELP.responseTime}
              delta={d(t.p50DurationMs, p?.p50DurationMs, 'down', 'Response time p50')}
              sub={t.avgSteps != null ? `p50 / p95 · ${t.avgSteps.toFixed(1)} steps` : 'p50 / p95'}
            />
          )}
          {t.failedQueries !== undefined && (
            <Kpi
              label="Failed queries"
              value={fmt(t.failedQueries)}
              help={HELP.failedQueries}
              delta={d(t.failedQueries, p?.failedQueries, 'down', 'Failed queries')}
            />
          )}
        </>
      )}
      {fb && (
        <Kpi
          label="Feedback"
          value={fb.up + fb.down > 0 ? pct(fb.ratio ?? fb.up / (fb.up + fb.down)) : '—'}
          help={HELP.feedback}
          delta={d(fb.ratio, p?.feedback?.ratio, 'up', 'Positive feedback')}
          sub={`${fmt(fb.up)} up · ${fmt(fb.down)} down`}
        />
      )}
      {t.errorRate !== undefined && (
        <Kpi
          label="Error rate"
          value={pct(t.errorRate)}
          help={HELP.errorRate}
          delta={d(t.errorRate, p?.errorRate, 'down', 'Error rate')}
          sub="server errors ÷ requests"
        />
      )}
    </div>
  );
}

/** Same page, same environment: Buddy links keep `?target=`. */
export const buddyPath = (path: string, target?: string) =>
  target ? `${path}${path.includes('?') ? '&' : '?'}target=${encodeURIComponent(target)}` : path;
